import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ApiPolicy, ApiTraveler, ApiAddress, ApiFlight, ApiQuoteRequest, ApiPackageRequest, IApiAddress } from '@bhtp-client-api/base';
import { BhtpPurchaseService, ApiSmartQuoteResponse } from '@bhtp-client-api/purchase';
import { BhtpStateService, State, BhtpJurisdictionsService } from '@bhtp-client-api/eligibility';
import { PurchaseData, Flight, Traveler, FeatureService, AmbassadorInformationSessionStorage, Agent } from './../shared';
import { ModalService } from '@bhtp-client-web/component';

@Component({
    selector: 'quick-quote-container',
    template: require('./quick-quote-container.component.html')
})
export class QuickQuoteContainerComponent implements OnInit {
    @Input() quote: PurchaseData = new PurchaseData();
    @Input() maxTravelers: number = 25;
    @Input() showFlightSegments: boolean = true;
    @Input() maxDate: string;
    @Input() agent: Agent;

    @Output() validQuote: EventEmitter<ApiSmartQuoteResponse> = new EventEmitter<ApiSmartQuoteResponse>();
    @Output() temporaryQuoteChanged: EventEmitter<PurchaseData> = new EventEmitter<PurchaseData>();
    @Output() valuesChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() stateChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() agentCodeChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() serviceCall: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() serviceError: EventEmitter<string> = new EventEmitter<string>();


    private currentAgentCode: string;
    private addressResult: IApiAddress;

    constructor(private purchaseService: BhtpPurchaseService,
                private bhtpStateService: BhtpStateService,
                private featureService: FeatureService,
                private ambassadorService: AmbassadorInformationSessionStorage,
                private jurisdictionsService: BhtpJurisdictionsService) {}

    public ngOnInit(): void {
        // Get the agent code if an Ambassador is helping an agent
        if (this.ambassadorService.getAgentCode()) {
            this.currentAgentCode = this.ambassadorService.getAgentCode();
        }
        this.showFlightSegments = this.featureService.flightSegmentsIsEnabled();

        // If they get to this page and already have purchase data from the dashboard
        // then we need to check the postal code.
        if (this.quote && this.quote.postalCode) {
            this.onPostalCodeChanged(this.quote.postalCode);
        }
    }

    public onValuesChanged(changed: boolean): void {
        this.valuesChanged.emit(changed);
    }

    public onStateChanged(newState: string): void {
        this.stateChanged.emit(newState);
    }

    public onPostalCodeChanged(newPostalCode: string): void {
        this.jurisdictionsService
            .searchResidenceByPostalCode(newPostalCode, 'US')
            .subscribe(result => {
                this.processPostalCodeResult(result, newPostalCode);
            });
    }

    public onAgentCodeChanged(newAgentCode: string): void {
        this.currentAgentCode = newAgentCode;
        this.ambassadorService.setAgentCode(this.currentAgentCode);
        this.agentCodeChanged.emit(this.currentAgentCode);
    }

    public onTemporaryQuoteChanged(tempQuote: PurchaseData): void {
        this.temporaryQuoteChanged.emit(tempQuote);
    }

    public onValidQuote(quote: PurchaseData): void {
        quote.residenceState = this.addressResult.stateOrProvince;

        let request: ApiQuoteRequest = new ApiQuoteRequest();
        request.policy = new ApiPolicy();
        request.policy.departureDate = quote.departureDate;
        request.policy.returnDate = quote.returnDate;
        request.policy.tripDepositDate = quote.depositDate;
        request.policy.destinationCountry = quote.destinationCountry ? quote.destinationCountry.isoCode2 : null;
        request.policy.isQuickQuote = true;
        request.travelers = new Array<ApiTraveler>();

        let primaryTraveler: ApiTraveler = new ApiTraveler();
        primaryTraveler.birthDate = quote.primaryTraveler.birthDate;
        primaryTraveler.tripCost = quote.primaryTraveler.tripCost;
        primaryTraveler.gender = null;
        primaryTraveler.isPrimary = true;
        primaryTraveler.address = new ApiAddress();
        primaryTraveler.address = this.addressResult;

        request.travelers.push(primaryTraveler);

        quote.additionalTravelers.forEach(at => {
            let additionalTraveler: ApiTraveler = new ApiTraveler();
            additionalTraveler.tripCost = at.tripCost;
            additionalTraveler.birthDate = at.birthDate;
            additionalTraveler.gender = null;

            request.travelers.push(additionalTraveler);
        });

        if (quote.flights) {
            request.flights = new Array<ApiFlight>();
            for (let i = 0; i < quote.flights.length; i++) {
                request.flights.push(new ApiFlight());
            }
        }

        // *Hey Listen, if this is an ambassador, and they provided an agent code we are
        // going to quote those packages for which the provided agent has licences for.
        if (this.agent && this.agent.isAmbassador && this.currentAgentCode) {
            request.policy.agentCode = this.currentAgentCode;
            request.policy.agencyCode = this.ambassadorService.getAgencyCode();
        }

        this.serviceCall.emit(true);
        this.purchaseService.getAgentSmartQuote(request).subscribe(
            (smartResponse) => {
                this.validQuote.emit(smartResponse);
            }, (error: any) => {
                this.serviceError.emit('There was an error while quoting.');
            },
            () => {
                this.serviceCall.emit(false);
            }
        );
    }

    private processPostalCodeResult(result, newPostalCode: string) {
        let stateOrProvince: string = null;
        if (!result || result.validState !== 1) {
            result.result = new ApiAddress();
            result.result['error'] = 'Invalid Postal Code ' + newPostalCode;
        }
        else {
            stateOrProvince = result.result.stateOrProvince;
        }
        this.quote.primaryTraveler.address = this.addressResult = result.result;
        this.quote.residenceState = stateOrProvince;
        this.stateChanged.emit(stateOrProvince);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/quick-quote/quick-quote-container.component.ts